import { LANGUAGE_OPTIONS } from "../i18n/languages";
import { BOER_LANDING_COPY, BOER_LANDING_LOCALES, boerLandingLocale, type BoerLandingLocale } from "./boerCopy";
import { CONTACT_CSS, CONTACT_MARKUP, CONTACT_RUNTIME } from "./boerContact";

const PLAN_HOT_CSS = BOER_LANDING_LOCALES
  .filter((code) => code !== "en")
  .map((code) => {
    const label = BOER_LANDING_COPY[code]["plan.mostFarms"];
    return `html[data-locale="${code}"] .plan.hot::after { content: ${JSON.stringify(label)}; }`;
  })
  .join("\n  ");

const LOCALE_LABELS = Object.fromEntries(LANGUAGE_OPTIONS.map((l) => [l.code, l.label])) as Record<
  BoerLandingLocale,
  string
>;

function localeLabel(code: BoerLandingLocale): string {
  return LOCALE_LABELS[code] ?? code;
}

const TOGGLE_CSS = `
  .lang-toggle { position: relative; flex: 0 0 auto; }
  .lang-toggle__btn { appearance: none; display: inline-flex; align-items: center; gap: 6px; height: 38px; padding: 0 12px 0 14px; border: 1.5px solid var(--dune); border-radius: 999px; background: var(--paper); color: var(--ink); font-family: var(--body); font-size: .86rem; font-weight: 600; line-height: 1; cursor: pointer; white-space: nowrap; }
  .lang-toggle__btn:hover { border-color: var(--ink-soft); }
  .lang-toggle__btn[aria-expanded="true"] { border-color: var(--kelp); }
  .lang-toggle__name { max-width: 7.5rem; overflow: hidden; text-overflow: ellipsis; }
  /* Phone: the pill keeps the globe + chevron and drops the language NAME,
     reclaiming ~120px of a header row that could not afford it. 44px tall
     because it is a real control. This lives in TOGGLE_CSS deliberately —
     the page's own <style> block is in <body> and beats <head>. */
  @media (max-width: 700px) {
    .lang-toggle__name { display: none; }
    .lang-toggle__btn { height: 44px; padding: 0 12px; gap: 4px; }
  }
  .lang-toggle__code { font-family: var(--mono); font-size: .62rem; font-weight: 700; letter-spacing: .06em; color: var(--ink-mute); }
  .lang-toggle__chevron { color: var(--ink-mute); flex: 0 0 auto; transition: transform .15s ease; }
  .lang-toggle.is-open .lang-toggle__chevron { transform: rotate(180deg); }
  .lang-toggle__menu { position: absolute; top: calc(100% + 8px); right: 0; min-width: 13.5rem; padding: 6px; background: var(--paper); border: 1.5px solid var(--dune); border-radius: var(--radius); box-shadow: var(--shadow); z-index: 40; }
  .lang-toggle__menu[hidden] { display: none; }
  .lang-toggle__option { appearance: none; display: flex; align-items: center; justify-content: space-between; gap: 16px; width: 100%; padding: 9px 12px; border: 0; border-radius: 10px; background: transparent; color: var(--ink); font-family: var(--body); font-size: .9rem; font-weight: 600; cursor: pointer; text-align: left; }
  .lang-toggle__option:hover { background: var(--salt-deep); }
  .lang-toggle__option[aria-selected="true"] { background: var(--kelp-tint); color: var(--kelp); }
  .lang-toggle__option[aria-selected="true"] .lang-toggle__code { color: var(--kelp); }
  .phone-nav em { font-style: normal; font-weight: inherit; }
  ${PLAN_HOT_CSS}
`

const TOGGLE_MARKUP = `
      <div class="lang-toggle" data-lang-toggle>
        <button type="button" class="lang-toggle__btn" id="boer-lang-btn" aria-expanded="false" aria-haspopup="listbox" aria-controls="boer-lang-list" data-i18n-aria="lang.aria" aria-label="Language">
          <span class="lang-toggle__name" data-lang-current-name>${localeLabel("en")}</span>
          <span class="lang-toggle__code" data-lang-current-code>EN</span>
          <svg class="lang-toggle__chevron" viewBox="0 0 12 8" width="10" height="7" aria-hidden="true"><path d="M1 1.5L6 6.5L11 1.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="lang-toggle__menu" id="boer-lang-list" role="listbox" hidden>
${BOER_LANDING_LOCALES.map(
  (code) =>
    `          <button type="button" class="lang-toggle__option" role="option" data-landing-locale="${code}" aria-selected="${code === "en" ? "true" : "false"}"><span class="lang-toggle__option-name">${localeLabel(code)}</span><span class="lang-toggle__code">${code.toUpperCase()}</span></button>`,
).join("\n")}
        </div>
      </div>`;

/**
 * BOE-41 — the four former "Start free" buttons (header + grower plan share the
 * primary style, hero + final band share the band style). They no longer sign up:
 * pricing is under investigation, so they open the contact sheet as
 * "start-free" and the grower registers interest.
 */
const INTEREST_PRIMARY = 'class="btn btn-primary btn-interest" href="#contact" data-contact-context="start-free">Under investigation – prices still to be determined</a>';
const INTEREST_BAND = 'class="btn btn-band btn-interest" href="#contact" data-contact-context="start-free">Under investigation – prices still to be determined</a>';

/**
 * Source strings the landing repeats verbatim and that must be stamped at every
 * occurrence. The register card shows two "clear" chips (Tango + Star Ruby);
 * stamping only the first left the second in English for af (BOE-17). The
 * brand link appears in both header and footer. Nav/footer links are NOT here —
 * they carry different keys and are stamped separately below.
 */
const REPEATED_STAMPS = new Set<string>([
  '>To be determined</div>',
  '<span class="chip chip-ok">clear</span>',
  'aria-label="Boer home"',
  INTEREST_PRIMARY,
  INTEREST_BAND,
]);

/** Runtime the shell injects into the bundled Boer landing (srcdoc). */
export function boerLandingRuntime(initial: BoerLandingLocale): string {
  const copyJson = JSON.stringify(BOER_LANDING_COPY);
  const labelsJson = JSON.stringify(LOCALE_LABELS);
  return `<script>
(function () {
  var COPY = ${copyJson};
  var LABELS = ${labelsJson};
  var locale = ${JSON.stringify(initial)};

  function setOpen(open) {
    var wrap = document.querySelector("[data-lang-toggle]");
    if (!wrap) return;
    var btn = wrap.querySelector(".lang-toggle__btn");
    var menu = wrap.querySelector(".lang-toggle__menu");
    if (open) {
      wrap.classList.add("is-open");
      if (btn) btn.setAttribute("aria-expanded", "true");
      if (menu) menu.hidden = false;
    } else {
      wrap.classList.remove("is-open");
      if (btn) btn.setAttribute("aria-expanded", "false");
      if (menu) menu.hidden = true;
    }
  }

  function apply(next) {
    if (!COPY[next]) next = "en";
    locale = next;
    document.documentElement.lang = next;
    document.documentElement.setAttribute("data-locale", next);
    var nodes = document.querySelectorAll("[data-i18n]");
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var key = el.getAttribute("data-i18n");
      var val = COPY[next] && COPY[next][key];
      if (val == null) continue;
      if (el.getAttribute("data-i18n-html") === "1") el.innerHTML = val;
      else el.textContent = val;
    }
    var labels = document.querySelectorAll("[data-i18n-n]");
    for (var n = 0; n < labels.length; n++) {
      var nv = COPY[next] && COPY[next][labels[n].getAttribute("data-i18n-n")];
      if (nv) labels[n].setAttribute("data-n", nv);
    }
    var arias = document.querySelectorAll("[data-i18n-aria]");
    for (var j = 0; j < arias.length; j++) {
      var a = arias[j];
      var ak = a.getAttribute("data-i18n-aria");
      if (COPY[next] && COPY[next][ak]) a.setAttribute("aria-label", COPY[next][ak]);
    }
    var title = COPY[next] && COPY[next]["doc.title"];
    if (title) document.title = title;
    var nameEl = document.querySelector("[data-lang-current-name]");
    var codeEl = document.querySelector("[data-lang-current-code]");
    if (nameEl) nameEl.textContent = LABELS[next] || next;
    if (codeEl) codeEl.textContent = String(next).toUpperCase();
    var options = document.querySelectorAll("[data-landing-locale]");
    for (var k = 0; k < options.length; k++) {
      options[k].setAttribute("aria-selected", options[k].getAttribute("data-landing-locale") === next ? "true" : "false");
    }
    setOpen(false);
    try {
      parent.postMessage({ source: "metaship-landing", type: "locale", locale: next }, "*");
    } catch (e) { /* preview / file:// */ }
  }

  function onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    var wrap = document.querySelector("[data-lang-toggle]");
    setOpen(!(wrap && wrap.classList.contains("is-open")));
  }

  function onOptionClick(event) {
    event.preventDefault();
    event.stopPropagation();
    var next = event.currentTarget && event.currentTarget.getAttribute("data-landing-locale");
    if (next) apply(next);
  }

  /*
   * In-page links (BOE-3 / BOE-7 / BOE-13 / BOE-14). This document is a srcdoc
   * frame, and a srcdoc document resolves "#chain" against the PARENT's URL —
   * so a plain click navigated the sandboxed frame to the whole shell, which
   * then hung on "loading" and threw (no storage in an opaque origin). Every
   * same-page link is handled here instead: scroll to the target, or for the
   * #contact links open the contact sheet (BOE-40), told which button opened it
   * by its data-contact-context attribute. Sign-in links keep their own bridge.
   */
${CONTACT_RUNTIME}

  function setMenu(open) {
    var nav = document.querySelector(".nav");
    var btn = document.querySelector("[data-nav-menu]");
    if (nav) nav.classList.toggle("is-open", open);
    if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");
  }

  function onLinkClick(event) {
    var link = event.target && event.target.closest ? event.target.closest("a[href^='#']") : null;
    if (!link || link.hasAttribute("data-metaship-signin")) return;
    event.preventDefault();
    setMenu(false);
    var id = link.getAttribute("href").slice(1);
    if (id === "contact") {
      openContact(link.getAttribute("data-contact-context") || "contact");
      return;
    }
    if (!id || id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    var target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function start() {
    document.addEventListener("click", onLinkClick);
    startContact();
    var menuBtn = document.querySelector("[data-nav-menu]");
    if (menuBtn) {
      menuBtn.addEventListener("click", function (event) {
        event.stopPropagation();
        var nav = document.querySelector(".nav");
        setMenu(!(nav && nav.classList.contains("is-open")));
      });
    }
    var wrap = document.querySelector("[data-lang-toggle]");
    var btn = wrap && wrap.querySelector(".lang-toggle__btn");
    if (btn) btn.addEventListener("click", onTriggerClick);
    var options = document.querySelectorAll("[data-landing-locale]");
    for (var i = 0; i < options.length; i++) {
      options[i].addEventListener("click", onOptionClick);
    }
    document.addEventListener("click", function (event) {
      setOpen(false);
      var nav = document.querySelector(".nav");
      if (nav && !nav.contains(event.target)) setMenu(false);
    });
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") { setOpen(false); setMenu(false); closeContact(); }
    });
    window.addEventListener("message", function (event) {
      var data = event.data;
      if (!data || data.source !== "metaship-shell") return;
      if (data.type === "contact-result") { onContactMessage(data); return; }
      if (data.type !== "locale") return;
      apply(data.locale && COPY[data.locale] ? data.locale : "en");
    });
    apply(locale);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
</script>`;
}

/**
 * Stamp `data-i18n` (and the host-language control) onto the live Boer marketing HTML
 * fragment, wrap it as a document, and append the i18n + sign-in runtimes.
 */
export function prepareBoerLandingHtml(source: string, locale: string): string {
  const loc = boerLandingLocale(locale);
  let html = source;

  if (!html.includes(".lang-toggle {")) {
    html = html.replace("</style>", `${TOGGLE_CSS}\n${CONTACT_CSS}\n</style>`);
  }
  if (!html.includes("data-landing-locale")) {
    html = html.replace('<div class="nav-cta">', `<div class="nav-cta">${TOGGLE_MARKUP}\n`);
  }
  if (!html.includes("data-contact-form")) {
    html = `${html}\n${CONTACT_MARKUP}\n`;
  }

  const stamps: Array<[string, string]> = [
    ['<nav class="nav" aria-label="Primary">', '<nav class="nav" aria-label="Primary" data-i18n-aria="lang.aria">'],
    ['aria-label="Boer home"', 'aria-label="Boer home" data-i18n-aria="nav.homeAria"'],
    ['<a href="#chain">How it connects</a>', '<a href="#chain" data-i18n="nav.chain">How it connects</a>'],
    ['<a href="#product">Product</a>', '<a href="#product" data-i18n="nav.product">Product</a>'],
    ['<a href="#compare">Compare</a>', '<a href="#compare" data-i18n="nav.compare">Compare</a>'],
    ['<a href="#packhouses">For packhouses</a>', '<a href="#packhouses" data-i18n="nav.packhouses">For packhouses</a>'],
    ['<a href="#pricing">Pricing</a>', '<a href="#pricing" data-i18n="nav.pricing">Pricing</a>'],
    ['class="btn btn-ghost" href="#signin">Sign in</a>', 'class="btn btn-ghost" href="#signin" data-i18n="cta.signIn">Sign in</a>'],
    [INTEREST_PRIMARY, INTEREST_PRIMARY.replace('">', '" data-i18n="cta.underInvestigation">')],
    [INTEREST_BAND, INTEREST_BAND.replace('">', '" data-i18n="cta.underInvestigation">')],
    ['<span class="eyebrow">For South African fruit growers</span>', '<span class="eyebrow" data-i18n="hero.eyebrow">For South African fruit growers</span>'],
    [
      "<h1>The farm app that goes <span class=\"thread\">through the packhouse gate.</span></h1>",
      '<h1 data-i18n="hero.titleHtml" data-i18n-html="1">The farm app that goes <span class="thread">through the packhouse gate.</span></h1>',
    ],
    [
      '<p class="lead">Spray diary, harvest bins and export compliance on one record — from the orchard to the weighbridge and back to the block it came from. Built in the Western Cape, priced in Rand.</p>',
      '<p class="lead" data-i18n="hero.lead">Spray diary, harvest bins and export compliance on one record — from the orchard to the weighbridge and back to the block it came from. Built in the Western Cape, priced in Rand.</p>',
    ],
    ['class="btn btn-outline-light" href="#chain">See how it connects ↓</a>', 'class="btn btn-outline-light" href="#chain" data-i18n="cta.seeHow">See how it connects ↓</a>'],
    [
      '<p class="hero-note">No hectare limit. Works on the phone your foreman already carries.</p>',
      '<p class="hero-note" data-i18n="hero.note">No hectare limit. Works on the phone your foreman already carries.</p>',
    ],
    [
      '<span class="hero-credit">Franschhoek, Western Cape — footage © Paul Knipe</span>',
      '<span class="hero-credit" data-i18n="hero.credit">Franschhoek, Western Cape — footage © Paul Knipe</span>',
    ],
    [
      '<span class="eyebrow">From the block to the bill of lading</span>',
      '<span class="eyebrow" data-i18n="stats.eyebrow">From the block to the bill of lading</span>',
    ],
    [
      "<h2>One record. Six hand-offs. Nothing typed twice.</h2>",
      '<h2 data-i18n="stats.title">One record. Six hand-offs. Nothing typed twice.</h2>',
    ],
    [
      '<p class="lead">A field in a diary app is whatever someone typed. A block in Boer is the orchard the registry knows, the bin the gate scanned, and the kilograms the weighbridge saw.</p>',
      '<p class="lead" data-i18n="stats.lead">A field in a diary app is whatever someone typed. A block in Boer is the orchard the registry knows, the bin the gate scanned, and the kilograms the weighbridge saw.</p>',
    ],
    ["taps to log a spray at the row end", '<span data-i18n="stats.taps">taps to log a spray at the row end</span>'],
    ["hand-offs on the same record", '<span data-i18n="stats.handoffs">hand-offs on the same record</span>'],
    ["re-typing at the packhouse gate", '<span data-i18n="stats.retype">re-typing at the packhouse gate</span>'],
    ["languages, chosen once for the whole app", '<span data-i18n="stats.langs">languages, chosen once for the whole app</span>'],
    ['aria-label="Example bin record"', 'aria-label="Example bin record" data-i18n-aria="record.aria"'],
    ['<span class="chip chip-ok">Weighed</span>', '<span class="chip chip-ok" data-i18n="record.weighed">Weighed</span>'],
    ["Sprayed<small>Cyanamid · 14 days PHI</small>", '<span data-i18n="record.sprayed">Sprayed</span><small data-i18n="record.sprayedSub">Cyanamid · 14 days PHI</small>'],
    [">12 Jun · safe from 26 Jun</span>", ' data-i18n="record.sprayedVal">12 Jun · safe from 26 Jun</span>'],
    ["Picked<small>Team Willem · 6 pickers</small>", '<span data-i18n="record.picked">Picked</span><small data-i18n="record.pickedSub">Team Willem · 6 pickers</small>'],
    ["Sent<small>Dispatch note DN-2287 · 24 bins</small>", '<span data-i18n="record.sent">Sent</span><small data-i18n="record.sentSub">Dispatch note DN-2287 · 24 bins</small>'],
    ["Adopted at gate<small>RFID read · no re-typing</small>", '<span data-i18n="record.adopted">Adopted at gate</span><small data-i18n="record.adoptedSub">RFID read · no re-typing</small>'],
    ["Weighed<small>Packhouse weighbridge</small>", '<span data-i18n="record.weighedWhat">Weighed</span><small data-i18n="record.weighedSub">Packhouse weighbridge</small>'],
    ["Return<small>Posts back to this block</small>", '<span data-i18n="record.return">Return</span><small data-i18n="record.returnSub">Posts back to this block</small>'],
    [">after packout</span>", ' data-i18n="record.afterPackout">after packout</span>'],
    [">Block ZA-OB-1142-07 this week</span>", ' data-i18n="record.blockWeek">Block ZA-OB-1142-07 this week</span>'],
    [
      '<p class="label">Shaped for the registers South African growers already answer to.</p>',
      '<p class="label" data-i18n="trust.label">Shaped for the registers South African growers already answer to.</p>',
    ],
    ['<span class="eyebrow">Why growers switch</span>', '<span class="eyebrow" data-i18n="loop.eyebrow">Why growers switch</span>'],
    [
      "<h2>Everything else is a diary. Boer is the whole chain.</h2>",
      '<h2 data-i18n="loop.title">Everything else is a diary. Boer is the whole chain.</h2>',
    ],
    [
      "<p class=\"lead\">A diary app stops at a printed label. Boer names the bin at the pick, hands it through the gate, and brings the packhouse's weight back to the block it came from.</p>",
      '<p class="lead" data-i18n="loop.lead">A diary app stops at a printed label. Boer names the bin at the pick, hands it through the gate, and brings the packhouse\'s weight back to the block it came from.</p>',
    ],
    ['<b data-n="Block">The real orchard</b>', '<b data-n="Block" data-i18n-n="loop.blockN" data-i18n="loop.blockCap">The real orchard</b>'],
    [
      "<span>Registered PUC and orchard refs, cultivar codes, approved markets — not a shape someone drew.</span>",
      '<span data-i18n="loop.blockSpan">Registered PUC and orchard refs, cultivar codes, approved markets — not a shape someone drew.</span>',
    ],
    ['<b data-n="Pick">Logged at the row end</b>', '<b data-n="Pick" data-i18n-n="loop.pickN" data-i18n="loop.pickCap">Logged at the row end</b>'],
    [
      "<span>Spray cleared by PHI, bin named, team named, time stamped.</span>",
      '<span data-i18n="loop.pickSpan">Spray cleared by PHI, bin named, team named, time stamped.</span>',
    ],
    ['<b data-n="Gate">The bin goes through</b>', '<b data-n="Gate" data-i18n-n="loop.gateN" data-i18n="loop.gateCap">The bin goes through</b>'],
    [
      "<span>Dispatch note, RFID adoption at the gate, weighed and onto the line — no re-typing.</span>",
      '<span data-i18n="loop.gateSpan">Dispatch note, RFID adoption at the gate, weighed and onto the line — no re-typing.</span>',
    ],
    ['<b data-n="Return">Back to the block</b>', '<b data-n="Return" data-i18n-n="loop.returnN" data-i18n="loop.returnCap">Back to the block</b>'],
    [
      "<span>Packed, palletised, shipped — tonnes and Rands post back to the orchard and the team that picked it.</span>",
      '<span data-i18n="loop.returnSpan">Packed, palletised, shipped — tonnes and Rands post back to the orchard and the team that picked it.</span>',
    ],
    ["<small>ONE RECORD</small>Farm → gate → farm", '<small data-i18n="loop.oneRecord">ONE RECORD</small><span data-i18n="loop.farmGateFarm">Farm → gate → farm</span>'],
    ["<h3>Block</h3><p>Your registered orchard, synced from the registry, with cultivars and markets on the card.</p>", '<h3 data-i18n="chain.block">Block</h3><p data-i18n="chain.blockP">Your registered orchard, synced from the registry, with cultivars and markets on the card.</p>'],
    ["<h3>Spray</h3><p>Product, rate, water, operator, weather. The PHI sets the safe-harvest date itself.</p>", '<h3 data-i18n="chain.spray">Spray</h3><p data-i18n="chain.sprayP">Product, rate, water, operator, weather. The PHI sets the safe-harvest date itself.</p>'],
    ["<h3>Pick</h3><p>Every fill names a bin — block, cultivar, picker, time.</p>", '<h3 data-i18n="chain.pick">Pick</h3><p data-i18n="chain.pickP">Every fill names a bin — block, cultivar, picker, time.</p>'],
    ["<h3>Gate</h3><p>The packhouse gate reads the bin and adopts the fill. No paper, no missing bins.</p>", '<h3 data-i18n="chain.gate">Gate</h3><p data-i18n="chain.gateP">The packhouse gate reads the bin and adopts the fill. No paper, no missing bins.</p>'],
    ["<h3>Pack</h3><p>Weighed, graded, packed on the record you started.</p>", '<h3 data-i18n="chain.pack">Pack</h3><p data-i18n="chain.packP">Weighed, graded, packed on the record you started.</p>'],
    ["<h3>Return</h3><p>Tonnes and Rands come back to the block. Economics on real returns.</p>", '<h3 data-i18n="chain.return">Return</h3><p data-i18n="chain.returnP">Tonnes and Rands come back to the block. Economics on real returns.</p>'],
    ['<span class="eyebrow">The product</span>', '<span class="eyebrow" data-i18n="product.eyebrow">The product</span>'],
    [
      "<h2>Made for the person at the row end, not the one at the desk.</h2>",
      '<h2 data-i18n="product.title">Made for the person at the row end, not the one at the desk.</h2>',
    ],
    [
      "<p class=\"lead\">The foreman logs the spray at six in the morning, not the office at five in the afternoon. Boer is built for that — and the office gets a register that writes itself.</p>",
      '<p class="lead" data-i18n="product.lead">The foreman logs the spray at six in the morning, not the office at five in the afternoon. Boer is built for that — and the office gets a register that writes itself.</p>',
    ],
    ['<span class="eyebrow">Capture</span>', '<span class="eyebrow" data-i18n="capture.eyebrow">Capture</span>'],
    [
      "<h3>Three taps from the home screen. Readable in the sun.</h3>",
      '<h3 data-i18n="capture.title">Three taps from the home screen. Readable in the sun.</h3>',
    ],
    [
      "<p>Home, Farms, Harvest and one big <strong>+</strong>. Light screens, big targets, the block picked from where you're standing. Works offline in the orchard and syncs at the shed. <span class=\"season\">offline · season '26</span></p>",
      '<p data-i18n="capture.p">Home, Farms, Harvest and one big +. Light screens, big targets, the block picked from where you\'re standing. Works offline in the orchard and syncs at the shed. <span class="season" data-i18n="capture.offline">offline · season \'26</span></p>',
    ],
    ["<li>Spray with the product's L-number, rate, water and tank mix</li>", '<li data-i18n="capture.li1">Spray with the product\'s L-number, rate, water and tank mix</li>'],
    ["<li>Fertilise, fertigate, irrigate, scout, trap round, sanitation</li>", '<li data-i18n="capture.li2">Fertilise, fertigate, irrigate, scout, trap round, sanitation</li>'],
    ["<li>Harvest fill with the bin named, the team named, the time stamped</li>", '<li data-i18n="capture.li3">Harvest fill with the bin named, the team named, the time stamped</li>'],
    ['aria-label="Phone showing a spray job being captured"', 'aria-label="Phone showing a spray job being captured" data-i18n-aria="phone.aria"'],
    [">Spray job<span", ' data-i18n="phone.sprayJob">Spray job<span'],
    ['<span class="k">Product</span>', '<span class="k" data-i18n="phone.kProduct">Product</span>'],
    ['<span class="k">Rate · water</span>', '<span class="k" data-i18n="phone.kRate">Rate · water</span>'],
    ['<span class="k">Equipment · operator</span>', '<span class="k" data-i18n="phone.kEquip">Equipment · operator</span>'],
    ['<span class="k">Weather at 06:40</span>', '<span class="k" data-i18n="phone.kWeather">Weather at 06:40</span>'],
    ['<span class="k">Safe harvest from</span>', '<span class="k" data-i18n="phone.kSafe">Safe harvest from</span>'],
    ["<span><i></i>Home</span>", '<span><i></i><em data-i18n="phone.navHome">Home</em></span>'],
    ["<span><i></i>Farms</span>", '<span><i></i><em data-i18n="phone.navFarms">Farms</em></span>'],
    ['<span class="on"><i></i>Harvest</span>', '<span class="on"><i></i><em data-i18n="phone.navHarvest">Harvest</em></span>'],
    ["<span><i></i>More</span>", '<span><i></i><em data-i18n="phone.navMore">More</em></span>'],
    ['<span class="eyebrow">Registry truth</span>', '<span class="eyebrow" data-i18n="registry.eyebrow">Registry truth</span>'],
    ["<h3>Your blocks are the real blocks.</h3>", '<h3 data-i18n="registry.title">Your blocks are the real blocks.</h3>'],
    [
      "<p>Boer syncs your production units and orchards from the registry — PUC, orchard refs, cultivar codes, market registrations and certificate expiry. Change it once; the inspector and the packhouse see the same thing.</p>",
      '<p data-i18n="registry.p">Boer syncs your production units and orchards from the registry — PUC, orchard refs, cultivar codes, market registrations and certificate expiry. Change it once; the inspector and the packhouse see the same thing.</p>',
    ],
    ["<li>One block row per registered orchard, for life</li>", '<li data-i18n="registry.li1">One block row per registered orchard, for life</li>'],
    ["<li>Approved markets and GGN expiry on the block card</li>", '<li data-i18n="registry.li2">Approved markets and GGN expiry on the block card</li>'],
    ["<li>Import your KML or draw it on the map — the ref stays the same</li>", '<li data-i18n="registry.li3">Import your KML or draw it on the map — the ref stays the same</li>'],
    ["<small>ZA-PU-1142 · 4 orchards · GGN expires in 84 days</small>", '<small data-i18n="registry.cardSub">ZA-PU-1142 · 4 orchards · GGN expires in 84 days</small>'],
    ['<span class="chip chip-ok">Registry-synced</span>', '<span class="chip chip-ok" data-i18n="registry.synced">Registry-synced</span>'],
    ["<th>Orchard</th>", '<th data-i18n="registry.thOrchard">Orchard</th>'],
    ["<th>Cultivar</th>", '<th data-i18n="registry.thCultivar">Cultivar</th>'],
    ["<th>Markets</th>", '<th data-i18n="registry.thMarkets">Markets</th>'],
    ["<th>Safe harvest</th>", '<th data-i18n="registry.thSafe">Safe harvest</th>'],
    ['<span class="chip chip-warn">PHI not clear</span>', '<span class="chip chip-warn" data-i18n="registry.phiNotClear">PHI not clear</span>'],
    ['<span class="chip chip-ok">clear</span>', '<span class="chip chip-ok" data-i18n="registry.clear">clear</span>'],
    ['<span class="eyebrow">The bin loop</span>', '<span class="eyebrow" data-i18n="bins.eyebrow">The bin loop</span>'],
    [
      "<h3>Know what left the farm and what the packhouse received. To the kilogram.</h3>",
      '<h3 data-i18n="bins.title">Know what left the farm and what the packhouse received. To the kilogram.</h3>',
    ],
    [
      "<p>Empty, full, in store, sent, adopted at the gate, weighed. Custody moves with the bin; the weight comes back to you. When the packout differs from what you sent, you know which bin, which block, which morning.</p>",
      '<p data-i18n="bins.p">Empty, full, in store, sent, adopted at the gate, weighed. Custody moves with the bin; the weight comes back to you. When the packout differs from what you sent, you know which bin, which block, which morning.</p>',
    ],
    ["<li>Dispatch notes built from the bins you actually filled</li>", '<li data-i18n="bins.li1">Dispatch notes built from the bins you actually filled</li>'],
    ["<li>RFID or QR — the gate adopts the fill without re-typing</li>", '<li data-i18n="bins.li2">RFID or QR — the gate adopts the fill without re-typing</li>'],
    ["<li>Tonnage ladder per block, per cultivar, per team</li>", '<li data-i18n="bins.li3">Tonnage ladder per block, per cultivar, per team</li>'],
    ["<th>Bin</th>", '<th data-i18n="bins.thBin">Bin</th>'],
    ["<th>Block</th>", '<th data-i18n="bins.thBlock">Block</th>'],
    ["<th>Team</th>", '<th data-i18n="bins.thTeam">Team</th>'],
    ["<th>Gate</th>", '<th data-i18n="bins.thGate">Gate</th>'],
    ['<th class="num">Sent kg</th>', '<th class="num" data-i18n="bins.thSent">Sent kg</th>'],
    ['<th class="num">Weighed kg</th>', '<th class="num" data-i18n="bins.thWeighed">Weighed kg</th>'],
    ['<td colspan="4" style="color:var(--ink-mute)">… 20 more bins</td>', '<td colspan="4" style="color:var(--ink-mute)" data-i18n="bins.more">… 20 more bins</td>'],
    ['<span class="chip chip-ok">24 / 24 adopted</span>', '<span class="chip chip-ok" data-i18n="bins.adopted">24 / 24 adopted</span>'],
    ['<span class="eyebrow">Compliance</span>', '<span class="eyebrow" data-i18n="comp.eyebrow">Compliance</span>'],
    ["<h3>The register writes itself.</h3>", '<h3 data-i18n="comp.title">The register writes itself.</h3>'],
    [
      "<p>Trap rounds, orchard sanitation, safe-harvest dates and certificate expiry live on the same record as your sprays. When the auditor calls, the Locker shows what's ready and what isn't — and exports the register in the shape they ask for. <span class=\"season\">exports · season '26</span></p>",
      '<p data-i18n="comp.p">Trap rounds, orchard sanitation, safe-harvest dates and certificate expiry live on the same record as your sprays. When the auditor calls, the Locker shows what\'s ready and what isn\'t — and exports the register in the shape they ask for. <span class="season" data-i18n="comp.exports">exports · season \'26</span></p>',
    ],
    ["<li>FCM trap rounds and sanitation, anchored to your season dates</li>", '<li data-i18n="comp.li1">FCM trap rounds and sanitation, anchored to your season dates</li>'],
    ["<li>Safe-harvest board: which blocks may be picked today</li>", '<li data-i18n="comp.li2">Safe-harvest board: which blocks may be picked today</li>'],
    ["<li>GlobalG.A.P.-shaped spray register, harvest register, inspection pack</li>", '<li data-i18n="comp.li3">GlobalG.A.P.-shaped spray register, harvest register, inspection pack</li>'],
    ["Compliance Locker<small>Klipfontein · audit in 19 days</small>", 'Compliance Locker<small data-i18n="comp.auditIn">Klipfontein · audit in 19 days</small>'],
    ['<span class="chip chip-warn">2 to fix</span>', '<span class="chip chip-warn" data-i18n="comp.toFix">2 to fix</span>'],
    [">ready for the inspector</span>", ' data-i18n="comp.readyFor">ready for the inspector</span>'],
    ["Spray register<small>212 applications · every one with an L-number</small>", '<span data-i18n="comp.sprayReg">Spray register</span><small data-i18n="comp.sprayRegSub">212 applications · every one with an L-number</small>'],
    [">complete</span>", ' data-i18n="comp.complete">complete</span>'],
    ["FCM trap rounds<small>Weekly since 1 Oct · 14 traps</small>", '<span data-i18n="comp.fcm">FCM trap rounds</span><small data-i18n="comp.fcmSub">Weekly since 1 Oct · 14 traps</small>'],
    ["Orchard sanitation<small>Fallen-fruit register</small>", '<span data-i18n="comp.san">Orchard sanitation</span><small data-i18n="comp.sanSub">Fallen-fruit register</small>'],
    [">last: 2 days ago</span>", ' data-i18n="comp.lastDays">last: 2 days ago</span>'],
    ["Safe-harvest board<small>Block 03 · PHI clears 9 Jul</small>", '<span data-i18n="comp.safeBoard">Safe-harvest board</span><small data-i18n="comp.safeBoardSub">Block 03 · PHI clears 9 Jul</small>'],
    [">1 block not clear</span>", ' data-i18n="comp.oneBlock">1 block not clear</span>'],
    ["Document vault<small>SIZA, water-use licence, FBO</small>", '<span data-i18n="comp.vault">Document vault</span><small data-i18n="comp.vaultSub">SIZA, water-use licence, FBO</small>'],
    [">11 documents</span>", ' data-i18n="comp.docs">11 documents</span>'],
    ["GlobalG.A.P. certificate<small>GGN 4049929…</small>", '<span data-i18n="comp.ggap">GlobalG.A.P. certificate</span><small>GGN 4049929…</small>'],
    [">expires in 84 days</span>", ' data-i18n="comp.expires">expires in 84 days</span>'],
    ['<span class="eyebrow">Compare</span>', '<span class="eyebrow" data-i18n="compare.eyebrow">Compare</span>'],
    ["<h2>Good diaries. One chain.</h2>", '<h2 data-i18n="compare.title">Good diaries. One chain.</h2>'],
    [
      "<p class=\"lead\">Farmable is a fine spray diary and Tend is a fine crop planner. Neither knows your PUC, neither follows the bin through the gate, and neither brings the weight back.</p>",
      '<p class="lead" data-i18n="compare.lead">Farmable is a fine spray diary and Tend is a fine crop planner. Neither knows your PUC, neither follows the bin through the gate, and neither brings the weight back.</p>',
    ],
    ['<th class="boer">Boer<small>Saltbird · South Africa</small></th>', '<th class="boer"><span data-i18n="compare.thBoer">Boer</span><small data-i18n="compare.thBoerSub">Saltbird · South Africa</small></th>'],
    ["<th>Farmable<small>Oslo, Norway</small></th>", '<th><span data-i18n="compare.thFarmable">Farmable</span><small data-i18n="compare.thFarmableSub">Oslo, Norway</small></th>'],
    ["<th>Tend<small>California, USA</small></th>", '<th><span data-i18n="compare.thTend">Tend</span><small data-i18n="compare.thTendSub">California, USA</small></th>'],
    [
      "Blocks are your registered orchards<small>PUC · orchard refs · cultivar codes · markets</small>",
      '<span data-i18n="compare.r1">Blocks are your registered orchards</span><small data-i18n="compare.r1s">PUC · orchard refs · cultivar codes · markets</small>',
    ],
    ['<span class="yes">✓</span> Registry-synced', '<span class="yes">✓</span> <span data-i18n="compare.r1b">Registry-synced</span>'],
    ['<span class="no">—</span> Self-declared fields', '<span class="no">—</span> <span data-i18n="compare.r1f">Self-declared fields</span>'],
    ['<span class="no">—</span> Self-declared beds', '<span class="no">—</span> <span data-i18n="compare.r1t">Self-declared beds</span>'],
    [">Bin custody from pick to packhouse gate</td>", '><span data-i18n="compare.r2">Bin custody from pick to packhouse gate</span></td>'],
    ['<span class="yes">✓</span> Named bin, dispatch, gate adoption', '<span class="yes">✓</span> <span data-i18n="compare.r2b">Named bin, dispatch, gate adoption</span>'],
    [">Printed harvest label</td>", '><span data-i18n="compare.r2f">Printed harvest label</span></td>'],
    [">Lot ID and QR</td>", '><span data-i18n="compare.r2t">Lot ID and QR</span></td>'],
    [">Packhouse weights come back to the block</td>", '><span data-i18n="compare.r3">Packhouse weights come back to the block</span></td>'],
    [
      "FCM trap rounds and sanitation register<small>DALRRD-shaped, anchored to your season</small>",
      '<span data-i18n="compare.r4">FCM trap rounds and sanitation register</span><small data-i18n="compare.r4s">DALRRD-shaped, anchored to your season</small>',
    ],
    [">Generic monitoring job</td>", '><span data-i18n="compare.r4f">Generic monitoring job</span></td>'],
    [">Safe-harvest date from the PHI, per block</td>", '><span data-i18n="compare.r5">Safe-harvest date from the PHI, per block</span></td>'],
    [">Label alerts (full data UK only)</td>", '><span data-i18n="compare.r5f">Label alerts (full data UK only)</span></td>'],
    [">Certificate expiry and audit-readiness check</td>", '><span data-i18n="compare.r6">Certificate expiry and audit-readiness check</span></td>'],
    ['<span class="yes">✓</span> Compliance Locker', '<span class="yes">✓</span> <span data-i18n="compare.r6b">Compliance Locker</span>'],
    [">Compliance dashboard</td>", '><span data-i18n="compare.r6f">Compliance dashboard</span></td>'],
    [">Reports</td>", '><span data-i18n="compare.r6t">Reports</span></td>'],
    [
      "South African product register<small>Act 36 L-numbers on every spray</small>",
      '<span data-i18n="compare.r7">South African product register</span><small data-i18n="compare.r7s">Act 36 L-numbers on every spray</small>',
    ],
    ['<span class="no">—</span> 9 registers, none SA', '<span class="no">—</span> <span data-i18n="compare.r7f">9 registers, none SA</span>'],
    [">Spray diary on the phone, offline</td>", '><span data-i18n="compare.r8">Spray diary on the phone, offline</span></td>'],
    ['<span class="yes">✓</span> companion app', '<span class="yes">✓</span> <span data-i18n="compare.r8t">companion app</span>'],
    [">GlobalG.A.P.-shaped register export</td>", '><span data-i18n="compare.r9">GlobalG.A.P.-shaped register export</span></td>'],
    [">Partial</td>", '><span data-i18n="compare.r9t">Partial</span></td>'],
    [">Works with your packhouse's system</td>", '><span data-i18n="compare.r10">Works with your packhouse\'s system</span></td>'],
    ['<span class="yes">✓</span> Same record', '<span class="yes">✓</span> <span data-i18n="compare.r10b">Same record</span>'],
    [">Enterprise portal, separate</td>", '><span data-i18n="compare.r10f">Enterprise portal, separate</span></td>'],
    [
      "Price for a grower<small>per farm, per year</small>",
      '<span data-i18n="compare.r11">Price for a grower</span><small data-i18n="compare.r11s">per farm, per year</small>',
    ],
    [">To be determined</td>", '><span data-i18n="compare.r11b">To be determined</span></td>'],
    [">Free (5 jobs) · Pro R6 499</td>", '><span data-i18n="compare.r11f">Free (5 jobs) · Pro R6 499</span></td>'],
    [">Free · Pro ≈ R4 300 (USD)</td>", '><span data-i18n="compare.r11t">Free · Pro ≈ R4 300 (USD)</span></td>'],
    [
      '<p class="compare-note">Competitor details from their public websites, help centres and app-store listings, August 2026. Farmable and Tend are trademarks of their owners. Items marked <span class="season">season \'26</span> ship before the 2026 stone-fruit season.</p>',
      '<p class="compare-note" data-i18n="compare.note">Competitor details from their public websites, help centres and app-store listings, August 2026. Farmable and Tend are trademarks of their owners. Items marked season \'26 ship before the 2026 stone-fruit season.</p>',
    ],
    ['<span class="eyebrow">Already on Farmable?</span>', '<span class="eyebrow" data-i18n="onramp.eyebrow">Already on Farmable?</span>'],
    ["<h3>Bring it across in an afternoon.</h3>", '<h3 data-i18n="onramp.title">Bring it across in an afternoon.</h3>'],
    [
      "<p>Connect your Farmable account and your fields, products, spray history, teams and timesheets come in as Boer blocks and records. Keep both running until you're sure. <span class=\"season\">season '26</span></p>",
      '<p data-i18n="onramp.p">Connect your Farmable account and your fields, products, spray history, teams and timesheets come in as Boer blocks and records. Keep both running until you\'re sure. <span class="season">season \'26</span></p>',
    ],
    ["<li>Paste your Farmable API key</li>", '<li data-i18n="onramp.s1">Paste your Farmable API key</li>'],
    ["<li>Match your fields to your registered orchards — Boer suggests the pairs</li>", '<li data-i18n="onramp.s2">Match your fields to your registered orchards — Boer suggests the pairs</li>'],
    ["<li>Three seasons of history land on the right blocks</li>", '<li data-i18n="onramp.s3">Three seasons of history land on the right blocks</li>'],
    ['<span class="eyebrow">For packhouses and co-ops</span>', '<span class="eyebrow" data-i18n="ph.eyebrow">For packhouses and co-ops</span>'],
    ["<h3>Bring your whole supplier base.</h3>", '<h3 data-i18n="ph.title">Bring your whole supplier base.</h3>'],
    [
      "<p>Every grower who supplies you can be on Boer. Their sprays, safe-harvest dates and bins arrive on your record before the truck does. One register for a hundred farms — and the returns you post go straight back to their blocks.</p>",
      '<p data-i18n="ph.p">Every grower who supplies you can be on Boer. Their sprays, safe-harvest dates and bins arrive on your record before the truck does. One register for a hundred farms — and the returns you post go straight back to their blocks.</p>',
    ],
    ["<li>Your supplier register becomes their farm list</li>", '<li data-i18n="ph.s1">Your supplier register becomes their farm list</li>'],
    ["<li>Your gate adopts their bins; your weighbridge closes the loop</li>", '<li data-i18n="ph.s2">Your gate adopts their bins; your weighbridge closes the loop</li>'],
    ["<li>Your packout and returns post back to the grower's block</li>", '<li data-i18n="ph.s3">Your packout and returns post back to the grower\'s block</li>'],
    ['href="#contact" data-contact-context="packhouse" style="justify-self:start">Talk to us about your packhouse</a>', 'href="#contact" data-contact-context="packhouse" style="justify-self:start" data-i18n="cta.talkPackhouse">Talk to us about your packhouse</a>'],
    ['<span class="eyebrow">Pricing</span>', '<span class="eyebrow" data-i18n="pricing.eyebrow">Pricing</span>'],
    [
      "<h2>Prices still to be determined. Rand, per farm, nothing per hectare.</h2>",
      '<h2 data-i18n="pricing.title">Prices still to be determined. Rand, per farm, nothing per hectare.</h2>',
    ],
    [
      "<p class=\"lead\">A farm is a farm — one PUC or six, ten workers or two hundred. The price doesn't move when the orchard grows.</p>",
      '<p class="lead" data-i18n="pricing.lead">A farm is a farm — one PUC or six, ten workers or two hundred. The price doesn\'t move when the orchard grows.</p>',
    ],
    ['<div class="name">Grower</div>', '<div class="name" data-i18n="plan.grower">Grower</div>'],
    [
      '<p class="who">Every grower, whether or not your packhouse is on Boer.</p>',
      '<p class="who" data-i18n="plan.growerWho">Every grower, whether or not your packhouse is on Boer.</p>',
    ],
    ["<li>Unlimited blocks, users and notes</li>", '<li data-i18n="plan.g1">Unlimited blocks, users and notes</li>'],
    ["<li>Spray, nutrition, irrigation and scouting records</li>", '<li data-i18n="plan.g2">Spray, nutrition, irrigation and scouting records</li>'],
    ["<li>FCM trap rounds, sanitation, safe-harvest board</li>", '<li data-i18n="plan.g3">FCM trap rounds, sanitation, safe-harvest board</li>'],
    ["<li>Harvest bins, dispatch, gate adoption, weights back</li>", '<li data-i18n="plan.g4">Harvest bins, dispatch, gate adoption, weights back</li>'],
    ["<li>Registry sync, certificate expiry, document vault</li>", '<li data-i18n="plan.g5">Registry sync, certificate expiry, document vault</li>'],
    ['>To be determined</div>', ' data-i18n="plan.tbd">To be determined</div>'],
    ['<div class="name">Pro</div>', '<div class="name" data-i18n="plan.pro">Pro</div>'],
    [
      '<p class="who">For the farm that gets audited.</p>',
      '<p class="who" data-i18n="plan.proWho">For the farm that gets audited.</p>',
    ],
    ["<li>Everything in Grower</li>", '<li data-i18n="plan.p1">Everything in Grower</li>'],
    ["<li>Audit exports: spray register, FCM report, harvest register, inspection pack</li>", '<li data-i18n="plan.p2">Audit exports: spray register, FCM report, harvest register, inspection pack</li>'],
    ["<li>Product inventory with cost per block</li>", '<li data-i18n="plan.p3">Product inventory with cost per block</li>'],
    ["<li>Clock-in, timesheets and piece rate</li>", '<li data-i18n="plan.p4">Clock-in, timesheets and piece rate</li>'],
    ["<li>Advisor access and block economics on real returns</li>", '<li data-i18n="plan.p5">Advisor access and block economics on real returns</li>'],
    ["<li>API access</li>", '<li data-i18n="plan.p6">API access</li>'],
    ['class="btn btn-primary" href="#signup">Start Pro</a>', 'class="btn btn-primary" href="#signup" data-i18n="cta.startPro">Start Pro</a>'],
    ['<div class="name">Packhouse &amp; co-op</div>', '<div class="name" data-i18n="plan.pack">Packhouse &amp; co-op</div>'],
    [">Let's talk</div>", ' data-i18n="plan.letsTalk">Let\'s talk</div>'],
    [
      '<p class="who">Your supplier base on one register, your gate and weighbridge closing the loop.</p>',
      '<p class="who" data-i18n="plan.packWho">Your supplier base on one register, your gate and weighbridge closing the loop.</p>',
    ],
    ["<li>Every supplying grower on Grower</li>", '<li data-i18n="plan.pk1">Every supplying grower on Grower</li>'],
    ["<li>Supplier compliance view before the truck arrives</li>", '<li data-i18n="plan.pk2">Supplier compliance view before the truck arrives</li>'],
    ["<li>Gate and weighbridge integration</li>", '<li data-i18n="plan.pk3">Gate and weighbridge integration</li>'],
    ["<li>Returns posted back to growers' blocks</li>", '<li data-i18n="plan.pk4">Returns posted back to growers\' blocks</li>'],
    ["<li>Onboarding on site, in Afrikaans or English</li>", '<li data-i18n="plan.pk5">Onboarding on site, in Afrikaans or English</li>'],
    ['class="btn btn-ghost" href="#contact" data-contact-context="pricing">Talk to us</a>', 'class="btn btn-ghost" href="#contact" data-contact-context="pricing" data-i18n="cta.talkToUs">Talk to us</a>'],
    [
      '<p class="price-note">Prices are still to be determined — in South African Rand, per farm, never per hectare or per user. Cancel any time; your data exports with you.</p>',
      '<p class="price-note" data-i18n="pricing.note">Prices are still to be determined — in South African Rand, per farm, never per hectare or per user. Cancel any time; your data exports with you.</p>',
    ],
    ['<span class="eyebrow">Questions growers ask</span>', '<span class="eyebrow" data-i18n="faq.eyebrow">Questions growers ask</span>'],
    ["<h2>Straight answers.</h2>", '<h2 data-i18n="faq.title">Straight answers.</h2>'],
    ["<summary>Does it work with no signal in the orchard?</summary>", '<summary data-i18n="faq.q1">Does it work with no signal in the orchard?</summary>'],
    [
      "<p>Harvest fills already queue offline and sync at the shed. Every other capture — sprays, trap rounds, scouting — goes offline before the 2026 stone-fruit season.</p>",
      '<p data-i18n="faq.a1">Harvest fills already queue offline and sync at the shed. Every other capture — sprays, trap rounds, scouting — goes offline before the 2026 stone-fruit season.</p>',
    ],
    ["<summary>Do I need my packhouse to be on Boer?</summary>", '<summary data-i18n="faq.q2">Do I need my packhouse to be on Boer?</summary>'],
    [
      "<p>No. Boer is a complete farm record on its own. When your packhouse joins, the gate and weighbridge close the loop automatically — nothing changes on your side.</p>",
      '<p data-i18n="faq.a2">No. Boer is a complete farm record on its own. When your packhouse joins, the gate and weighbridge close the loop automatically — nothing changes on your side.</p>',
    ],
    ["<summary>Who owns my data?</summary>", '<summary data-i18n="faq.q3">Who owns my data?</summary>'],
    [
      "<p>You do. It's stored in South Africa, handled under POPIA, and you can export everything at any time. Your packhouse sees only what you share with it.</p>",
      '<p data-i18n="faq.a3">You do. It\'s stored in South Africa, handled under POPIA, and you can export everything at any time. Your packhouse sees only what you share with it.</p>',
    ],
    ["<summary>I'm on Farmable. Do I lose my history?</summary>", '<summary data-i18n="faq.q4">I\'m on Farmable. Do I lose my history?</summary>'],
    [
      "<p>No. Connect your Farmable account and three seasons of fields, sprays, teams and timesheets come across onto your registered orchards. Keep both running until you're sure.</p>",
      '<p data-i18n="faq.a4">No. Connect your Farmable account and three seasons of fields, sprays, teams and timesheets come across onto your registered orchards. Keep both running until you\'re sure.</p>',
    ],
    ["<summary>Which crops?</summary>", '<summary data-i18n="faq.q5">Which crops?</summary>'],
    [
      "<p>Citrus, stone fruit, pome fruit, table grapes, avocados and macadamias first — the export crops with FCM, PHI and GlobalG.A.P. pressure. Other crops work; the statutory registers are tuned for these.</p>",
      '<p data-i18n="faq.a5">Citrus, stone fruit, pome fruit, table grapes, avocados and macadamias first — the export crops with FCM, PHI and GlobalG.A.P. pressure. Other crops work; the statutory registers are tuned for these.</p>',
    ],
    ["<summary>Which languages are available?</summary>", '<summary data-i18n="faq.q6">Which languages are available?</summary>'],
    [
      "<p>English, Afrikaans, Español, Italiano and Japanese. Use the language control at the top of this page. The signed-in app follows the same choice — language is set once on this host.</p>",
      '<p data-i18n="faq.a6">English, Afrikaans, Español, Italiano and Japanese. Use the language control at the top of this page. The signed-in app follows the same choice — language is set once on this host.</p>',
    ],
    ["<h2>Start with one block this week.</h2>", '<h2 data-i18n="final.title">Start with one block this week.</h2>'],
    [
      "<p>Add your PUC, log Monday's spray, fill a bin on the first pick. By the end of the season the register is written and the packhouse has already seen it.</p>",
      '<p data-i18n="final.p">Add your PUC, log Monday\'s spray, fill a bin on the first pick. By the end of the season the register is written and the packhouse has already seen it.</p>',
    ],
    ['<a href="#product">Product</a>', '<a href="#product" data-i18n="foot.product">Product</a>'],
    ['<a href="#compare">Compare</a>', '<a href="#compare" data-i18n="foot.compare">Compare</a>'],
    ['<a href="#pricing">Pricing</a>', '<a href="#pricing" data-i18n="foot.pricing">Pricing</a>'],
    ['<a href="#packhouses">Packhouses</a>', '<a href="#packhouses" data-i18n="foot.packhouses">Packhouses</a>'],
    ['<a href="#changelog">What\'s new</a>', '<a href="#changelog" data-i18n="foot.whatsNew">What\'s new</a>'],
    ['<a href="#privacy">Privacy</a>', '<a href="#privacy" data-i18n="foot.privacy">Privacy</a>'],
    ['<a href="#terms">Terms</a>', '<a href="#terms" data-i18n="foot.terms">Terms</a>'],
    ['<a href="#contact" data-contact-context="contact">Contact</a>', '<a href="#contact" data-contact-context="contact" data-i18n="foot.contact">Contact</a>'],
    [
      '<span class="saltbird">Boer is a Saltbird company · Made in the Western Cape</span>',
      '<span class="saltbird" data-i18n="foot.saltbird">Boer is a Saltbird company · Made in the Western Cape</span>',
    ],
    [
      "<span>© 2026 Saltbird. Boer is independent and not affiliated with AgriOps, DALRRD, GlobalG.A.P., SIZA or PPECB. Photography: Freshness First, used with permission. Franschhoek footage © Paul Knipe.</span>",
      '<span data-i18n="foot.legal">© 2026 Saltbird. Boer is independent and not affiliated with AgriOps, DALRRD, GlobalG.A.P., SIZA or PPECB. Photography: Freshness First, used with permission. Franschhoek footage © Paul Knipe.</span>',
    ],
  ];

  for (const [from, to] of stamps) {
    if (!html.includes(from)) continue;
    html = REPEATED_STAMPS.has(from) ? html.replaceAll(from, to) : html.replace(from, to);
  }

  // Footer Product/Compare/Pricing appear twice; stamp leftovers that still
  // match the un-annotated English (nav already stamped).
  html = html.replace('<a href="#product">Product</a>', '<a href="#product" data-i18n="foot.product">Product</a>');
  html = html.replace('<a href="#compare">Compare</a>', '<a href="#compare" data-i18n="foot.compare">Compare</a>');
  html = html.replace('<a href="#pricing">Pricing</a>', '<a href="#pricing" data-i18n="foot.pricing">Pricing</a>');

  const wrapped = html.includes("<!doctype") || html.includes("<html")
    ? html
    : `<!doctype html>
<html lang="${loc}" data-locale="${loc}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Boer by Saltbird</title>
${html.includes("<title>") ? "" : ""}
</head>
<body>
${html}
</body>
</html>`;

  // The live fragment already has <title> + <style> at the top. If we wrapped
  // it, move those into <head> by leaving them where they are — browsers hoist
  // title/style. Ensure html lang is set when the fragment is used as-is.
  let documentHtml = wrapped;
  if (!documentHtml.includes("<html")) {
    documentHtml = `<!doctype html><html lang="${loc}" data-locale="${loc}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body>${html}</body></html>`;
  } else {
    documentHtml = documentHtml.replace(/<html\b[^>]*>/, `<html lang="${loc}" data-locale="${loc}">`);
  }

  if (!documentHtml.includes("data-landing-locale")) {
    // stamp failed — still inject toggle CSS/markup via a last-resort prepend
    documentHtml = documentHtml.replace('<div class="nav-cta">', `<div class="nav-cta">${TOGGLE_MARKUP}`);
  }

  if (!documentHtml.includes("metaship-landing")) {
    // Keep the uploaded document's own sign-in bridge if present.
  }

  if (!documentHtml.includes("data-landing-locale")) {
    throw new Error("Boer landing stamp failed: language toggle was not inserted");
  }

  return `${documentHtml}\n${boerLandingRuntime(loc)}`;
}
